import React, {useEffect, useState} from "react"
// MUI
import {
    Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
    FormControl, InputLabel, MenuItem, Select, Typography,
} from "@mui/material"
// Data
import {useData} from "@/context/PackagingDataProvider"
// Tokens
import {packagingTokens as tk} from "@/theme/packagingTokens"

type DeleteAppDialogProps = {
    open: boolean
    onClose: () => void
    // The app being deleted (list item: {id, name, brands, default}).
    app: any
    // All apps (to offer transfer targets).
    apps: any[]
    // Called after a successful deletion (refresh the list).
    onDeleted: () => void
    t: (k: string, o?: any) => string
}

// Confirm deletion of an app. When the app has associated brands, offers to
// transfer them to another existing app first (default: detach them).
export default function DeleteAppDialog(props: DeleteAppDialogProps) {
    const {open, onClose, app, apps, onDeleted, t} = props
    const {deleteApp} = useData()

    const [transferTo, setTransferTo] = useState<string>("")
    const [deleting, setDeleting] = useState<boolean>(false)

    useEffect(() => {
        if (open) setTransferTo("")
    }, [open])

    const brandsCount = app?.brands?.length ?? 0
    const targets = (apps ?? []).filter((a: any) => a.id !== app?.id)
    const appLabel = (a: any) => a?.name || t("app_untitled")

    const handleDelete = async () => {
        setDeleting(true)
        try {
            const deleted = await deleteApp(app.id, transferTo || undefined)
            if (deleted) {
                onDeleted()
                onClose()
            }
        } finally {
            setDeleting(false)
        }
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth={"xs"} fullWidth>
            <DialogTitle sx={{fontSize: "18px", fontWeight: 600}}>
                {t("app_delete")}{app?.name ? ` — ${app.name}` : ""}
            </DialogTitle>
            <DialogContent>
                <Typography sx={{fontSize: "13.5px", color: tk.color.textSecondary, mb: 2}}>
                    {t("app_delete_text")}
                </Typography>

                {brandsCount > 0 && (
                    <Box
                        sx={{
                            px: 2, py: 1.75, mb: 2,
                            borderRadius: tk.radius.card,
                            backgroundColor: tk.color.amber100,
                            border: `1px solid ${tk.color.amber}33`,
                        }}
                    >
                        <Typography sx={{fontSize: "13px", fontWeight: 600, color: tk.color.textPrimary, mb: 0.5}}>
                            {t("app_delete_brands_notice", {num: brandsCount})}
                        </Typography>
                        <Typography sx={{fontSize: "12.5px", color: tk.color.textSecondary, mb: 1.5}}>
                            {t("app_delete_transfer_hint")}
                        </Typography>
                        <FormControl fullWidth size={"small"}>
                            <InputLabel id={"transfer-app-select"}>{t("app_delete_transfer_label")}</InputLabel>
                            <Select
                                label={t("app_delete_transfer_label")}
                                labelId={"transfer-app-select"}
                                value={transferTo}
                                onChange={(e) => setTransferTo(e.target.value as string)}
                                sx={{backgroundColor: tk.color.surface}}
                            >
                                <MenuItem value={""}>
                                    <em>{t("app_delete_no_transfer")}</em>
                                </MenuItem>
                                {targets.map((a: any) => (
                                    <MenuItem key={a.id} value={a.id}>
                                        {appLabel(a)}{a.default ? ` · ${t("default")}` : ""}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                )}

                <Alert severity={"error"} sx={{borderRadius: tk.radius.card}}>
                    {t("common:delete_irreversible_alert")}
                </Alert>
            </DialogContent>
            <DialogActions sx={{px: 3, pb: 2.5}}>
                <Button onClick={onClose} sx={{textTransform: "none", color: tk.color.textSecondary}}>
                    {t("common:cancel")}
                </Button>
                <Button
                    variant={"contained"}
                    color={"error"}
                    disableElevation
                    disabled={deleting}
                    onClick={handleDelete}
                    sx={{textTransform: "none", fontWeight: 600, borderRadius: tk.radius.sm}}
                >
                    {t("common:delete")}
                </Button>
            </DialogActions>
        </Dialog>
    )
}
